import React, { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { SlidersHorizontal, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  DASHBOARD_SECTION_IDS,
  DASHBOARD_SECTION_LABELS,
  type DashboardSectionId,
  type DashboardSectionVisibility,
} from './hooks/useDashboardSectionVisibility';

interface DashboardSectionFilterProps {
  visibility: DashboardSectionVisibility;
  onToggle: (id: DashboardSectionId) => void;
  onShowAll: () => void;
  onHideAll: () => void;
}

export const DashboardSectionFilter: React.FC<DashboardSectionFilterProps> = ({
  visibility,
  onToggle,
  onShowAll,
  onHideAll,
}) => {
  const [open, setOpen] = useState(false);
  const visibleCount = DASHBOARD_SECTION_IDS.filter(id => visibility[id]).length;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <div className="flex justify-end">
        <DialogPrimitive.Trigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200/90 bg-white/90 px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-all hover:border-indigo-300 hover:bg-indigo-50/80 hover:shadow dark:border-slate-600 dark:bg-slate-800/90 dark:text-slate-100 dark:hover:border-indigo-500 dark:hover:bg-indigo-950/40"
          >
            <SlidersHorizontal className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden />
            Filtre
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              {visibleCount}/{DASHBOARD_SECTION_IDS.length}
            </span>
          </button>
        </DialogPrimitive.Trigger>
      </div>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[2100] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'bg-slate-900/35 backdrop-blur-[2px]'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[2101] w-[calc(100%-1.5rem)] max-w-lg -translate-x-1/2 -translate-y-1/2',
            'max-h-[min(90vh,640px)] overflow-y-auto rounded-2xl border border-slate-200/90 bg-white p-5 shadow-2xl',
            'focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'dark:border-slate-600 dark:bg-slate-900'
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <DialogPrimitive.Title className="text-lg font-semibold text-slate-900 dark:text-white">
                Görünüm filtresi
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Arka planda dashboard görünür. Hangi bölümlerin listeleneceğini seçin; ayarlar kaydedilir.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Kapat"
              >
                <X className="h-5 w-5" />
              </button>
            </DialogPrimitive.Close>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onShowAll}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Tümünü aç
              </button>
              <button
                type="button"
                onClick={onHideAll}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Tümünü kapat
              </button>
            </div>
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {DASHBOARD_SECTION_IDS.map(id => (
                <li key={id}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors',
                      visibility[id]
                        ? 'border-indigo-200 bg-indigo-50/90 dark:border-indigo-800 dark:bg-indigo-950/50'
                        : 'border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/50'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={visibility[id]}
                      onChange={() => onToggle(id)}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
                    />
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {DASHBOARD_SECTION_LABELS[id]}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
